/**
 * Admin information architecture (design/04 §1, T11-01). Every menu entry names who may use it
 * (`access` — permission strings from shop_common.auth.Permission, design/04 §14); the same rule
 * guards the route, so a hidden entry opened by URL shows the plain "no access" page.
 *
 * Channels (eBay/Whatnot/…) and Analytics are not main-menu groups in phase 1 (R18): they are
 * "coming soon" entries under Settings.
 */
import {
  BadgePercent,
  Boxes,
  ClipboardList,
  FileText,
  Handshake,
  House,
  Layers,
  type LucideIcon,
  Palette,
  Settings,
  Store,
  Users,
} from "lucide-react";

import { type AccessRule, allows } from "@/auth/access";
import type { Permission } from "@/auth/types";
import { env } from "@/lib/env";

export type NavGroupKey =
  | "home"
  | "orders"
  | "products"
  | "inventory"
  | "customers"
  | "consignment"
  | "pos"
  | "discounts"
  | "content"
  | "online_store"
  | "settings";

export interface NavItem {
  /** i18n key under `nav.items.*` — also the page title. */
  key: string;
  /** Absolute path (or URL for `external`). */
  to: string;
  access?: AccessRule;
  /** Opens another app (the POS) — not a route of the admin. */
  external?: boolean;
  /** Phase-gated (R18): shown with a "coming soon" tag, the page says so. */
  comingSoon?: boolean;
  /** NavLink `end` (exact match). */
  end?: boolean;
}

export interface NavGroup {
  key: NavGroupKey;
  icon: LucideIcon;
  items: readonly NavItem[];
}

const any = (...permissions: Permission[]): AccessRule => ({ anyOf: permissions });
const need = (permission: Permission): AccessRule => ({ permission });

export const NAV: readonly NavGroup[] = [
  { key: "home", icon: House, items: [{ key: "home", to: "/", end: true }] },
  {
    key: "orders",
    icon: ClipboardList,
    items: [
      { key: "orders_all", to: "/orders", access: need("orders.read"), end: true },
      {
        key: "orders_bank_transfers",
        to: "/orders/bank-transfers",
        access: need("payments.confirm"),
      },
      { key: "orders_drafts", to: "/orders/drafts", access: need("orders.update") },
      { key: "orders_pickups", to: "/orders/pickups", access: need("orders.fulfill") },
      { key: "orders_returns", to: "/orders/returns", access: need("orders.update") },
      {
        key: "orders_export_evidence",
        to: "/orders/export-evidence",
        access: need("tax.read"),
      },
      {
        // T13-16 電帳法 search (also a tab of the tax screens) — in the menu of the people who
        // keep the books; the page itself opens with any document read permission (orders.read …)
        key: "orders_documents",
        to: "/settings/tax/documents",
        access: any("tax.read", "reports.view", "kobutsu.export"),
      },
    ],
  },
  {
    key: "products",
    icon: Layers,
    items: [
      {
        key: "products_all",
        to: "/products",
        access: any("products.write", "products.publish"),
        end: true,
      },
      {
        key: "products_catalog",
        to: "/products/catalog",
        access: any("catalog.write", "products.write"),
      },
      {
        key: "products_collections",
        to: "/products/collections",
        access: any("collections.write", "products.write"),
      },
      { key: "products_categories", to: "/products/categories", access: need("catalog.write") },
      { key: "products_brands", to: "/products/brands", access: need("catalog.write") },
      { key: "products_attributes", to: "/products/attributes", access: need("catalog.write") },
      { key: "products_import_export", to: "/products/import-export", access: need("import.run") },
    ],
  },
  {
    key: "inventory",
    icon: Boxes,
    items: [
      {
        key: "inventory_stock",
        to: "/inventory",
        access: any("inventory.receive", "inventory.adjust", "inventory.holds.manage"),
        end: true,
      },
      {
        key: "inventory_movements",
        to: "/inventory/movements",
        access: any("inventory.receive", "inventory.adjust", "inventory.holds.manage"),
      },
      { key: "inventory_receipts", to: "/inventory/receipts", access: need("inventory.receive") },
      {
        key: "inventory_transfers",
        to: "/inventory/transfers",
        access: need("inventory.transfer"),
      },
      { key: "inventory_counts", to: "/inventory/counts", access: need("inventory.count") },
      {
        key: "inventory_holds",
        to: "/inventory/holds",
        access: any("inventory.holds.release", "inventory.holds.manage"),
      },
      {
        key: "inventory_conflicts",
        to: "/inventory/conflicts",
        access: need("inventory.conflicts.resolve"),
      },
      {
        key: "inventory_live_shows",
        to: "/inventory/live-shows",
        access: need("inventory.live_show.manage"),
      },
      {
        key: "inventory_locations",
        to: "/inventory/locations",
        access: any("inventory.receive", "inventory.adjust"),
      },
    ],
  },
  {
    key: "customers",
    icon: Users,
    items: [
      {
        key: "customers_all",
        to: "/customers",
        access: any("customers.write", "consignment.read"),
        end: true,
      },
      { key: "customers_groups", to: "/customers/groups", access: need("customers.write") },
      {
        key: "customers_store_credit",
        to: "/customers/store-credit",
        access: need("payments.read"),
      },
    ],
  },
  {
    key: "consignment",
    icon: Handshake,
    items: [
      {
        key: "consignment_consignors",
        to: "/consignment/consignors",
        access: need("consignment.read"),
      },
      {
        key: "consignment_items",
        to: "/consignment/items",
        access: any("consignment.read", "consignment.intake"),
      },
      {
        key: "consignment_settlements",
        to: "/consignment/settlements",
        access: need("consignment.manage"),
      },
      { key: "consignment_payouts", to: "/consignment/payouts", access: need("payouts.create") },
      {
        key: "consignment_requests",
        to: "/consignment/requests",
        access: need("consignment.manage"),
      },
      {
        key: "consignment_kobutsu",
        to: "/consignment/kobutsu-ledger",
        access: need("kobutsu.read"),
      },
    ],
  },
  {
    key: "pos",
    icon: Store,
    items: [
      { key: "pos_open", to: env.VITE_POS_URL, access: need("pos.sell"), external: true },
      { key: "pos_registers", to: "/pos/registers", access: need("pos.devices.pair") },
      { key: "pos_shifts", to: "/pos/shifts", access: need("pos.shift.manage") },
      { key: "pos_buylist", to: "/pos/buylist", access: need("buylist.accept") },
      { key: "pos_cash", to: "/pos/cash-movements", access: need("pos.shift.manage") },
    ],
  },
  {
    key: "discounts",
    icon: BadgePercent,
    items: [{ key: "discounts", to: "/discounts", access: need("discounts.write") }],
  },
  {
    key: "content",
    icon: FileText,
    items: [
      { key: "content_pages", to: "/content/pages", access: need("cms.edit") },
      { key: "content_policies", to: "/content/policies", access: need("cms.edit") },
      { key: "content_menus", to: "/content/menus", access: need("cms.edit") },
      { key: "content_media", to: "/content/media", access: need("cms.edit") },
      { key: "content_announcements", to: "/content/announcements", access: need("cms.edit") },
      { key: "content_redirects", to: "/content/redirects", access: need("cms.edit") },
      { key: "content_translations", to: "/content/translations", access: need("cms.edit") },
      { key: "content_website_texts", to: "/content/website-texts", access: need("cms.edit") },
    ],
  },
  {
    key: "online_store",
    icon: Palette,
    items: [
      { key: "online_store_themes", to: "/online-store/themes", access: need("cms.edit") },
      { key: "online_store_languages", to: "/online-store/languages", access: need("cms.edit") },
      { key: "online_store_markets", to: "/online-store/markets", access: need("tax.read") },
      { key: "online_store_seo", to: "/online-store/seo", access: need("cms.edit") },
      { key: "online_store_domains", to: "/online-store/domains", access: need("settings.read") },
      // T11-46 "System → Website refresh": about the website, so website editors find it too
      {
        key: "settings_website_refresh",
        to: "/settings/website-refresh",
        access: need("cms.edit"),
      },
    ],
  },
  {
    key: "settings",
    icon: Settings,
    items: [
      { key: "settings_store", to: "/settings/store", access: need("settings.read") },
      { key: "settings_tax", to: "/settings/tax", access: any("tax.settings", "tax.read") },
      { key: "settings_shipping", to: "/settings/shipping", access: need("settings.read") },
      { key: "settings_payments", to: "/settings/payments", access: need("payments.read") },
      { key: "settings_checkout", to: "/settings/checkout", access: need("settings.read") },
      {
        key: "settings_notifications",
        to: "/settings/notifications",
        access: need("notifications.read"),
      },
      { key: "settings_inventory", to: "/settings/inventory", access: need("settings.read") },
      { key: "settings_pos", to: "/settings/pos", access: need("settings.read") },
      // T11-45: staff, roles and keys are the owner's (design/04 §14 staff.manage / api_keys)
      { key: "settings_staff", to: "/settings/staff", access: need("staff.manage") },
      { key: "settings_roles", to: "/settings/roles", access: need("staff.manage") },
      { key: "settings_api_keys", to: "/settings/api-keys", access: need("api_keys.manage") },
      // T11-46
      { key: "settings_audit", to: "/settings/audit-log", access: need("audit.view") },
      { key: "settings_jobs", to: "/settings/jobs", access: need("settings.read") },
      { key: "settings_features", to: "/settings/features", access: need("settings.read") },
      {
        key: "settings_channels",
        to: "/settings/channels",
        access: need("settings.read"),
        comingSoon: true,
      },
      {
        key: "settings_analytics",
        to: "/settings/analytics",
        access: need("reports.view"),
        comingSoon: true,
      },
    ],
  },
];

/** Screens outside the menu tree (header, user menu). */
export const EXTRA_ITEMS: readonly NavItem[] = [
  { key: "help", to: "/help" },
  { key: "profile", to: "/profile" },
  { key: "inbox", to: "/inbox", access: need("inbox.read") },
];

export interface VisibleGroup extends NavGroup {
  /** Where the group heading / icon goes: its first allowed internal entry. */
  to: string;
}

/** The menu for this staff member: entries without the permission are removed, empty groups too. */
export function filterNav(can: (permission: Permission) => boolean): VisibleGroup[] {
  const out: VisibleGroup[] = [];
  for (const group of NAV) {
    const items = group.items.filter((item) => allows(item.access, can));
    const first = items.find((item) => !item.external && !item.comingSoon) ?? items[0];
    if (first) {
      out.push({ ...group, items, to: first.to });
    }
  }
  return out;
}

/** Bottom navigation (tablet/phone): Home, Orders, Products, Inventory (when allowed) + More. */
export const BOTTOM_NAV_GROUPS: readonly NavGroupKey[] = [
  "home",
  "orders",
  "products",
  "inventory",
];

/** Every internal route entry (for the router and tests). */
export function allNavItems(): NavItem[] {
  return [...NAV.flatMap((group) => group.items), ...EXTRA_ITEMS].filter((item) => !item.external);
}

/** The group a path belongs to (longest matching entry wins). */
export function groupOfPath(pathname: string): NavGroupKey | null {
  let best: { key: NavGroupKey; length: number } | null = null;
  for (const group of NAV) {
    for (const item of group.items) {
      if (item.external) {
        continue;
      }
      const matches =
        item.to === "/"
          ? pathname === "/"
          : pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (matches && (!best || item.to.length > best.length)) {
        best = { key: group.key, length: item.to.length };
      }
    }
  }
  return best?.key ?? null;
}
